/**
 * **Layer 2 — Field:** `Field.Root`, optional label, validation error text, and the UI kit `Input`.
 * **`registration` is optional** — omit for local state / demos; pass `register('field')` for react-hook-form.
 */

import { Box, Field, defineStyle, type TextProps } from '@chakra-ui/react';
import { useId, useState, type ChangeEvent, type FocusEvent, type ReactElement } from 'react';
import { type FieldError, type UseFormRegisterReturn } from 'react-hook-form';
import { Input, type NovaInputProps } from './Input';

export type FormInputProps = Omit<NovaInputProps, 'id'> & {
  label?: string;
  labelPosition?: TextProps['textAlign'];
  className?: string;
  error?: FieldError | undefined;
  type?: 'text' | 'email' | 'password' | 'date';
  registration?: Partial<UseFormRegisterReturn>;
  textTransform?: TextProps['textTransform'];
  /** When `label` is set, defaults to `label` so `Field.Label` `htmlFor` matches the control. */
  id?: string;
};

const floatingLabelStyles = defineStyle({
  position: 'absolute',
  top: '10px',
  insetInlineStart: '36px',
  px: '1',
  pointerEvents: 'none',
  transitionProperty: 'top, inset-inline-start, color, transform',
  transitionDuration: 'fast',
  transitionTimingFunction: 'ease',
  transformOrigin: 'left top',
  textStyle: 'body.m.regular',
  color: 'text.tertiary',
  bg: 'background.secondary',
  '&[data-float]': {
    top: '-8px',
    insetInlineStart: '8px',
    transform: 'scale(0.9)',
    color: 'text.secondary',
  },
});

export const FormInput = (props: FormInputProps): ReactElement => {
  const {
    type = 'text',
    label,
    className,
    registration,
    error,
    placeholder,
    rightElement,
    leftElement,
    disabled = false,
    labelPosition,
    textTransform = 'none',
    id: idProp,
    bg,
    bgColor,
    ...inputProps
  } = props;

  const generatedId = useId();
  const controlId = idProp ?? registration?.name ?? generatedId;
  const surfaceBg = bg ?? bgColor ?? 'background.secondary';
  const [focused, setFocused] = useState(false);
  const [hasUncontrolledValue, setHasUncontrolledValue] = useState(
    () => String(inputProps.defaultValue ?? '').trim().length > 0
  );
  const controlledValue = inputProps.value;
  const hasControlledValue =
    controlledValue !== undefined && String(controlledValue).trim().length > 0;
  const hasValue = hasControlledValue || hasUncontrolledValue;

  //  Focus & blur forwarding with interceptors to extract the correct status for floating label
  const handleFocus = (event: FocusEvent<HTMLInputElement>): void => {
    setFocused(true);
    inputProps.onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    setFocused(false);
    inputProps.onBlur?.(event);
    registration?.onBlur?.(event);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    // Update the uncontrolled value status when the input value changes
    if (controlledValue === undefined) {
      setHasUncontrolledValue(event.target.value.trim().length > 0);
    }
    inputProps.onChange?.(event);
    registration?.onChange?.(event);
  };

  return (
    <Field.Root invalid={!!error?.message} gap="2xs" width="full">
      <Box position="relative" width="full">
        <Input
          {...inputProps}
          bg={surfaceBg}
          id={controlId}
          type={type}
          textOverflow="ellipsis"
          disabled={disabled}
          placeholder={placeholder}
          leftElement={leftElement}
          rightElement={rightElement}
          textTransform={textTransform}
          {...registration}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          data-float={hasValue || focused ? '' : undefined}
        />
        {label ? (
          <Field.Label
            css={floatingLabelStyles}
            htmlFor={controlId}
            className={className}
            textAlign={labelPosition}
            data-float={hasValue || focused ? '' : undefined}
          >
            {label}
          </Field.Label>
        ) : null}
      </Box>
      {error?.message ? (
        <Field.ErrorText px="s" textStyle="body.xs.regular" color="text.error">
          {error.message}
        </Field.ErrorText>
      ) : null}
    </Field.Root>
  );
};
